import { useEffect, useRef } from 'react';
import { supabase } from '../supabase/config';
import { useAuth } from '../contexts/AuthContext';

// Sheldon support agent — embedded from featurereq. Renders nothing itself;
// it loads the Sheldon loader script and initialises the floating widget once
// the user is signed in. The token is minted on demand by our Supabase edge
// function (sheldon-token), which featurereq verifies and matches by email.
// featurereq production. (You reach featurereq itself via a Schoolbox remote
// module, but the app is served from this origin and that's where sheldon.js
// and the widget iframe load from.)
const SHELDON_BASE = 'https://featurereq.vercel.app';

declare global {
  interface Window {
    Sheldon?: {
      init: (opts: Record<string, unknown>) => void;
      __loaded?: boolean;
    };
  }
}

export default function SheldonWidget() {
  const { user } = useAuth();
  const startedRef = useRef(false);

  useEffect(() => {
    if (!user || startedRef.current) return;
    startedRef.current = true;

    const getToken = async (): Promise<string | null> => {
      try {
        const { data, error } = await supabase.functions.invoke('sheldon-token');
        if (error || !data?.token) return null;
        return data.token as string;
      } catch {
        return null;
      }
    };

    const start = () => {
      window.Sheldon?.init({
        app: 'pit',
        baseUrl: SHELDON_BASE,
        getToken,
        // Tell Sheldon the user's PIT role so it tailors guidance (e.g. doesn't
        // walk a teacher through admin-only functions).
        userRole: user?.role ?? null,
        // Lift the floating button so it doesn't cover the footer/version.
        offsetBottom: 80,
      });
    };

    if (window.Sheldon?.__loaded) {
      start();
      return;
    }

    const existing = document.getElementById('sheldon-loader');
    if (existing) {
      existing.addEventListener('load', start);
      return;
    }

    const script = document.createElement('script');
    script.id = 'sheldon-loader';
    script.src = `${SHELDON_BASE}/sheldon.js`;
    script.async = true;
    script.addEventListener('load', start);
    document.body.appendChild(script);
  }, [user]);

  return null;
}
